import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ProgressBarModule } from 'primeng/progressbar';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { DashboardService } from '../services/dashboard.service';
import { AdminDashboardData, ReservationStatus, SubscriberPerformanceData } from '../models/dashboard.model';
import { PerformanceScoreService, PerformanceMetrics } from '../../../core/services/performance-score.service';
import { SubscriptionContextService } from '../../../core/services/subscription-context.service';
import { TermsConditionsModalComponent } from "../../user/terms-conditions-modal/terms-conditions-modal.component";


@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ChartModule,
    ButtonModule,
    TagModule,
    AvatarModule,
    AvatarGroupModule,
    ProgressBarModule,
    DividerModule,
    SelectModule,
    DatePickerModule,
    TooltipModule,
    ProgressSpinnerModule,
    SkeletonModule,
    TermsConditionsModalComponent
],
  templateUrl: './admin-dashboard.html',
  styleUrl: './admin-dashboard.scss'
})
export class AdminDashboard implements OnInit {
  dashboardData: AdminDashboardData | null = null;
  loading = true;

  // Date filter properties
  selectedPeriod: 'today' | 'thisWeek' | 'thisMonth' | 'last30Days' | 'custom' | null = null;
  dateFilterOptions = [
    { label: 'All Time', value: null },
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'thisWeek' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last 30 Days', value: 'last30Days' },
    { label: 'Custom Range', value: 'custom' }
  ];
  
  // Custom date range
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;
  showCustomDatePicker = false;
  
  // Date filter status
  isDateFilterActive = false;
  dateFilterDescription = 'All Time';

  // Active subscriber from context
  activeSubscriberId: string | null = null;
  activeSubscriberName: string = 'Current';

  // Chart options
  performanceChartOptions: any;
  reservationStatusOptions: any;
  reservationStatusChartData: any;
  subscriberPerformanceChartData: any;
  subscriberPerformanceChartOptions: any;

  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private performanceService: PerformanceScoreService,
    private subscriptionContextService: SubscriptionContextService
  ) {
    this.initializeChartOptions();
  }

  ngOnInit() {
    // Subscribe to active subscriber changes
    this.subscriptionContextService.selectedSubscriber$.subscribe(context => {
      this.activeSubscriberId = context.subscriberId;
      this.activeSubscriberName = context.companyName || 'Current';
      console.log('Admin Dashboard: Active subscriber ID:', this.activeSubscriberId);
    });
    
    // Initialize date filter (no filter by default - show all time)
    this.updateDateFilterStatus();
    this.loadDashboardData();
  }

  private initializeChartOptions() {
    this.performanceChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100
        }
      }
    };

    this.reservationStatusOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
        }
      }
    };

    this.subscriberPerformanceChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Count'
          }
        },
        x: {
          title: {
            display: false
          }
        }
      }
    };
  }

  private loadDashboardData() {
    this.loading = true;
    
    const dateFilter = this.getDateFilter();
    
    this.dashboardService.getUnifiedDashboardData(dateFilter).subscribe({
      next: (data) => {
        this.dashboardData = data;
        // Update performance chart to show reservation statuses over time (like FSR dashboard)
        this.updatePerformanceChartWithReservationData();
        this.reservationStatusChartData = {
          labels: data.reservationStatus.map(s => s.status),
          datasets: [{
            data: data.reservationStatus.map(s => s.count),
            backgroundColor: data.reservationStatus.map(s => s.color)
          }]
        };
        // Update subscriber performance chart for active subscriber
        this.updateSubscriberPerformanceChart();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading dashboard data:', error);
        this.loading = false;
        // Show error message instead of mock data
        this.dashboardData = null;
      }
    });
  }

  private getDateFilter(): { startDate?: string, endDate?: string, period?: 'today' | 'thisWeek' | 'thisMonth' | 'last30Days' | 'custom' } | undefined {
    // Return undefined if no filter is selected (show all time)
    if (!this.selectedPeriod) {
      return undefined;
    }
    
    if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
      return {
        startDate: this.customStartDate.toISOString().split('T')[0],
        endDate: this.customEndDate.toISOString().split('T')[0],
        period: 'custom'
      };
    }
    
    return {
      period: this.selectedPeriod
    };
  }

  onPeriodChange() {
    if (this.selectedPeriod === 'custom') {
      this.showCustomDatePicker = true;
      // Don't load data yet, wait for custom dates to be selected
    } else {
      this.showCustomDatePicker = false;
      this.customStartDate = null;
      this.customEndDate = null;
      this.updateDateFilterStatus();
      this.loadDashboardData();
    }
  }

  onCustomDateChange() {
    if (this.customStartDate && this.customEndDate) {
      // Validate that start date is not after end date
      if (this.customStartDate > this.customEndDate) {
        // Swap dates if start is after end
        const temp = this.customStartDate;
        this.customStartDate = this.customEndDate;
        this.customEndDate = temp;
      }
      this.updateDateFilterStatus();
      this.loadDashboardData();
    }
  }

  private updateDateFilterStatus() {
    const today = new Date();
    let isActive = false;
    let description = '';

    if (!this.selectedPeriod) {
      // No filter - show all time
      isActive = false;
      description = 'All Time';
    } else {
      switch (this.selectedPeriod) {
        case 'today':
          isActive = true;
          description = `Today (${today.toLocaleDateString()})`;
          break;
        case 'thisWeek':
          isActive = true;
          const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
          const endOfWeek = new Date(today.setDate(today.getDate() - today.getDay() + 6));
          description = `This Week (${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()})`;
          break;
        case 'thisMonth':
          isActive = true; // Show date range for this month
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          description = `This Month (${startOfMonth.toLocaleDateString()} - ${endOfMonth.toLocaleDateString()})`;
          break;
        case 'last30Days':
          isActive = true;
          const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
          description = `Last 30 Days (${thirtyDaysAgo.toLocaleDateString()} - ${new Date().toLocaleDateString()})`;
          break;
        case 'custom':
          if (this.customStartDate && this.customEndDate) {
            isActive = true;
            description = `Custom Range (${this.customStartDate.toLocaleDateString()} - ${this.customEndDate.toLocaleDateString()})`;
          } else {
            description = 'Custom Range (Select dates)';
          }
          break;
      }
    }

    this.isDateFilterActive = isActive;
    this.dateFilterDescription = description;
  }

  clearDateFilter() {
    this.selectedPeriod = null;
    this.customStartDate = null;
    this.customEndDate = null;
    this.showCustomDatePicker = false;
    this.updateDateFilterStatus();
    this.loadDashboardData();
  }

  // Refresh the subscriber performance chart when data changes
  refreshSubscriberChart() {
    this.updateSubscriberPerformanceChart();
  }


  getKpiIconClass(icon: string): string {
    return icon;
  }

  getTrendIconClass(direction: string): string {
    switch (direction) {
      case 'up':
        return 'pi pi-arrow-up text-green-500';
      case 'down':
        return 'pi pi-arrow-down text-red-500';
      default:
        return 'pi pi-minus text-gray-500';
    }
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'danger';
      default:
        return 'info';
    }
  }

  getPerformanceScoreColor(score: number): string {
    if (score >= 90) return '#10B981'; // green
    if (score >= 70) return '#F59E0B'; // yellow
    return '#EF4444'; // red
  }

  // Update performance chart to show reservations created vs contracts created per day
  private updatePerformanceChartWithReservationData() {
    if (!this.dashboardData) return;
    
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    // Calculate daily reservations created vs contracts created
    const performanceData = this.calculateDailyReservationsVsContracts();
    
    this.dashboardData.performanceChart = {
      labels: days,
      datasets: [
        {
          label: 'Reservations Created',
          data: performanceData.reservationsCreated,
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.3)',
          fill: false,
        },
        {
          label: 'Contracts Created',
          data: performanceData.contractsCreated,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.3)',
          fill: false,
        }
      ]
    };
  }

  // Calculate daily reservations created vs contracts created
  private calculateDailyReservationsVsContracts() {
    const dailyCounts = {
      reservationsCreated: new Array(7).fill(0),
      contractsCreated: new Array(7).fill(0)
    };
    
    // Get last 7 days
    const today = new Date();
    const last7Days: Date[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push(date);
    }
    
    // Count reservations created each day
    if (this.dashboardData?.reservations) {
      this.dashboardData.reservations.forEach(reservation => {
        // Assuming we have a createdAt field or similar
        const createdDate = new Date(reservation.expiryDate); // Replace with actual creation date field
        const dayIndex = last7Days.findIndex(date => 
          date.getDate() === createdDate.getDate() &&
          date.getMonth() === createdDate.getMonth() &&
          date.getFullYear() === createdDate.getFullYear()
        );
        
        if (dayIndex !== -1) {
          dailyCounts.reservationsCreated[dayIndex]++;
        }
      });
    }
    
    // Count contracts created each day
    if (this.dashboardData?.contracts) {
      this.dashboardData.contracts.forEach(contract => {
        const startDate = new Date(contract.startDate);
        const dayIndex = last7Days.findIndex(date => 
          date.getDate() === startDate.getDate() &&
          date.getMonth() === startDate.getMonth() &&
          date.getFullYear() === startDate.getFullYear()
        );
        
        if (dayIndex !== -1) {
          dailyCounts.contractsCreated[dayIndex]++;
        }
      });
    }
    
    return dailyCounts;
  }

  // Update subscriber performance chart for active subscriber
  private updateSubscriberPerformanceChart() {
    if (!this.dashboardData || !this.activeSubscriberId) {
      this.subscriberPerformanceChartData = {
        labels: ['No Data'],
        datasets: [
          {
            label: 'Contracts',
            data: [0],
            backgroundColor: 'rgba(16, 185, 129, 0.6)',
            borderColor: '#10B981',
            borderWidth: 1
          },
          {
            label: 'Reservations',
            data: [0],
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: '#3B82F6',
            borderWidth: 1
          }
        ]
      };
      return;
    }
    
    const subscriberData = this.getActiveSubscriberData();
    
    this.subscriberPerformanceChartData = {
      labels: [this.activeSubscriberName],
      datasets: [
        {
          label: 'Contracts',
          data: [subscriberData.contractCount],
          backgroundColor: 'rgba(16, 185, 129, 0.6)',
          borderColor: '#10B981',
          borderWidth: 1
        },
        {
          label: 'Reservations',
          data: [subscriberData.reservationCount],
          backgroundColor: 'rgba(59, 130, 246, 0.6)',
          borderColor: '#3B82F6',
          borderWidth: 1
        }
      ]
    };
  }

  // Get contracts and reservations data for the active subscriber
  getActiveSubscriberData(): SubscriberPerformanceData {
    if (!this.dashboardData || !this.activeSubscriberId) {
      return {
        subscriberId: this.activeSubscriberId || '',
        subscriberName: this.activeSubscriberName,
        companyName: this.activeSubscriberName,
        contractCount: 0,
        reservationCount: 0,
        conversionRate: 0,
        totalContractValue: 0
      };
    }
    
    let contractCount = 0;
    let reservationCount = 0;
    let totalContractValue = 0;
    
    // Count only ACTIVE contracts for active subscriber
    if (this.dashboardData.contracts) {
      this.dashboardData.contracts.forEach(contract => {
        // Filter: Only count contracts with status "ACTIVE" (active-approved contracts)
        if (contract.status === 'ACTIVE') {
          contractCount++;
          totalContractValue += contract.contractValue || 0;
        }
      });
    }
    
    // Use summaryStats.totalReservations for accurate count (reservations array is limited to 10)
    reservationCount = this.dashboardData.summaryStats?.totalReservations || 0;
    
    const conversionRate = reservationCount > 0 
      ? Math.round((contractCount / reservationCount) * 100)
      : 0;
    
    return {
      subscriberId: this.activeSubscriberId,
      subscriberName: this.activeSubscriberName,
      companyName: this.activeSubscriberName,
      contractCount,
      reservationCount,
      conversionRate,
      totalContractValue
    };
  }

  // Generate initials from first and last name
  getInitials(firstName: string, lastName: string): string {
    if (!firstName && !lastName) return 'UN';
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last;
  }

  // Get initials for business name (for customers)
  getBusinessInitials(businessName: string): string {
    if (!businessName) return 'BC';
    const words = businessName.trim().split(' ');
    if (words.length >= 2) {
      return words[0].charAt(0).toUpperCase() + words[1].charAt(0).toUpperCase();
    }
    return businessName.substring(0, 2).toUpperCase();
  }

  // Calculate performance score based on conversion rate and activity
  calculatePerformanceScore(fsr: any): number {
    if (!fsr) return 0;
    
    // Performance score calculation:
    // 40% conversion rate
    // 30% total reservations activity
    // 20% customer base size
    // 10% territory coverage
    
    const conversionWeight = 0.4;
    const activityWeight = 0.3;
    const customerWeight = 0.2;
    const territoryWeight = 0.1;
    
    // Normalize values to 0-100 scale
    const conversionScore = Math.min(fsr.performanceScore || 0, 100);
    const activityScore = Math.min(((fsr.totalReservations || 0) / 20) * 100, 100); // Assume 20+ reservations = 100%
    const customerScore = Math.min(((fsr.totalCustomers || 0) / 50) * 100, 100); // Assume 50+ customers = 100%
    const territoryScore = Math.min(((fsr.territories?.length || 0) / 5) * 100, 100); // Assume 5+ territories = 100%
    
    const totalScore = (
      conversionScore * conversionWeight +
      activityScore * activityWeight +
      customerScore * customerWeight +
      territoryScore * territoryWeight
    );
    
    return Math.round(totalScore);
  }

  // Navigation methods
  navigateToCustomerLookup(): void {
    this.router.navigate(['/admin/customer-lookup']);
  }

  navigateToCustomers(): void {
    this.router.navigate(['/admin/customers']);
  }

  navigateToCustomer(customerId: string): void {
    this.router.navigate(['/admin/view-customer', customerId]);
  }

  navigateToUsers(): void {
    this.router.navigate(['/admin/user-management']);
  }

  navigateToActiveFSRs(): void {
    this.router.navigate(['/admin/user-management'], {
      queryParams: {
        filterActiveFSRs: 'true'
      }
    });
  }

  navigateToContracts(): void {
    this.router.navigate(['/admin/contract-management']);
  }

  navigateToReservations(): void {
    this.router.navigate(['/admin/reservations']);
  }

  navigateToTerritories(): void {
    this.router.navigate(['/admin/territory-management']);
  }

  navigateToCalendar(): void {
    this.router.navigate(['/admin/calendar']);
  }

  navigateToProducts(): void {
    this.router.navigate(['/admin/products']);
  }

  navigateToSystemLogs(): void {
    this.router.navigate(['/admin/system-logs']);
  }

  navigateToNotifications(): void {
    this.router.navigate(['/admin/notifications']);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  formatDate(dateString: string | any): string {
    if (!dateString) {
      return 'Date not available';
    }
    
    let date: Date;
    
    // Handle MongoDB date object format: {"$date": "2025-10-06T14:07:07.732Z"}
    if (typeof dateString === 'object' && dateString.$date) {
      date = new Date(dateString.$date);
    } else if (typeof dateString === 'string') {
      date = new Date(dateString);
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Get performance breakdown for a specific FSR
  getPerformanceBreakdown(fsr: any): any {
    if (!fsr) return null;
    
    const conversionWeight = 0.4;
    const activityWeight = 0.3;
    const customerWeight = 0.2;
    const territoryWeight = 0.1;
    
    // Normalize values to 0-100 scale
    const conversionScore = Math.min(fsr.performanceScore || 0, 100);
    const activityScore = Math.min(((fsr.totalReservations || 0) / 20) * 100, 100);
    const customerScore = Math.min(((fsr.totalCustomers || 0) / 50) * 100, 100);
    const territoryScore = Math.min(((fsr.territories?.length || 0) / 5) * 100, 100);
    
    const weightedConversion = conversionScore * conversionWeight;
    const weightedActivity = activityScore * activityWeight;
    const weightedCustomer = customerScore * customerWeight;
    const weightedTerritory = territoryScore * territoryWeight;
    
    const totalScore = Math.round(weightedConversion + weightedActivity + weightedCustomer + weightedTerritory);
    
    return {
      totalScore,
      breakdown: {
        conversion: { raw: conversionScore, weighted: weightedConversion, weight: conversionWeight * 100 },
        activity: { raw: activityScore, weighted: weightedActivity, weight: activityWeight * 100 },
        customer: { raw: customerScore, weighted: weightedCustomer, weight: customerWeight * 100 },
        territory: { raw: territoryScore, weighted: weightedTerritory, weight: territoryWeight * 100 }
      }
    };
  }
}
